import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import InitiationLayout from '../../components/InitiationLayout';
import Badge from '../../components/Badge';
import {
  REQ_STATUS_LABEL,
  OOTB_CLASSIFICATION_LABEL,
  REQUIREMENT_TYPE_LABEL,
  NODE_REVIEW_STATUS_LABEL,
  T_SHIRT_SIZES,
  statusBadgeClass,
} from '../../lib/initiation';
import {
  startBpoReview,
  acceptBpoNodeReview,
  fetchOpenApprovalTasks,
} from '../../lib/tasks';
import {
  requestRequirementChange,
  fetchPendingChangeRequests,
} from '../../lib/requirementChangeControl';

function ootbScore(requirements) {
  if (!requirements.length) return null;
  const ootbLike = requirements.filter((r) =>
    r.ootb_classification === 'ootb' || r.ootb_classification === 'config'
  ).length;
  return Math.round((ootbLike / requirements.length) * 100);
}

function ReqRow({ req, nodesById, readOnly, baselineLocked, saving, onUpdate, approvalTask, focused }) {
  const node = nodesById.get(req.process_node_id);

  return (
    <tr className={`req-row${focused ? ' task-focus' : ''}`}>
      <td className="req-ref">{req.ref || '—'}</td>
      <td>
        <div className="req-title">{req.title}</div>
        {node && <div className="req-node-meta">{node.code}</div>}
        {approvalTask?.profiles?.full_name && req.status === 'bpo_review' && (
          <div className="req-node-meta">BPO task: {approvalTask.profiles.full_name}</div>
        )}
      </td>
      <td>
        <Badge variant={statusBadgeClass(req.status)}>{REQ_STATUS_LABEL[req.status] || req.status}</Badge>
      </td>
      <td>
        {baselineLocked ? (
          <span style={{ fontSize: 11, color: '#888780' }}>Per option →</span>
        ) : (
          <select
            className="req-inline-select"
            value={req.ootb_classification || ''}
            disabled={readOnly || saving}
            onChange={(e) => onUpdate(req.id, { ootb_classification: e.target.value || null })}
          >
            <option value="">—</option>
            {Object.entries(OOTB_CLASSIFICATION_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        )}
      </td>
      <td>
        {baselineLocked ? (
          <span style={{ fontSize: 11, color: '#888780' }}>Solution options</span>
        ) : (
          <select
            className="req-inline-select"
            value={req.t_shirt_size || ''}
            disabled={readOnly || saving || !['sa_sizing', 'sized', 'approved'].includes(req.status)}
            onChange={(e) => onUpdate(req.id, {
              t_shirt_size: e.target.value || null,
              status: e.target.value && req.status === 'sa_sizing' ? 'sized' : req.status,
              sized_at: e.target.value ? new Date().toISOString() : req.sized_at,
            })}
          >
            <option value="">—</option>
            {T_SHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </td>
      <td>
        <select
          className="req-inline-select"
          value={req.requirement_type || 'standard'}
          disabled={readOnly || saving}
          onChange={(e) => onUpdate(req.id, { requirement_type: e.target.value })}
        >
          {Object.entries(REQUIREMENT_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </td>
    </tr>
  );
}

export default function RequirementsRegister() {
  const { project, programme } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();
  const [searchParams] = useSearchParams();
  const focusReqId = searchParams.get('focusReq');

  const [scopeApproved, setScopeApproved] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [nodeReviews, setNodeReviews] = useState([]);
  const [nodesById, setNodesById] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalTasks, setApprovalTasks] = useState([]);
  const [toast, setToast] = useState(null);
  const [baselineLockedAt, setBaselineLockedAt] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [showChangeRequest, setShowChangeRequest] = useState(false);
  const [changeRationale, setChangeRationale] = useState('');

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [pinRes, projRes, reqsRes, reviewsRes] = await Promise.all([
        sb.from('project_taxonomy_pins').select('status').eq('project_id', project.id).maybeSingle(),
        sb.from('projects').select('requirements_baseline_locked_at').eq('id', project.id).maybeSingle(),
        sb.from('requirements')
          .select('*')
          .eq('project_id', project.id)
          .order('order_index')
          .order('ref'),
        sb.from('requirement_node_reviews').select('*').eq('project_id', project.id),
      ]);

      if (pinRes.error) throw pinRes.error;
      if (reqsRes.error) throw reqsRes.error;
      if (reviewsRes.error) throw reviewsRes.error;

      const reqs = reqsRes.data || [];
      setScopeApproved(pinRes.data?.status === 'approved');
      setBaselineLockedAt(projRes.data?.requirements_baseline_locked_at || null);
      setRequirements(reqs);
      setNodeReviews(reviewsRes.data || []);

      try {
        const taskRows = await fetchOpenApprovalTasks(project.id);
        setApprovalTasks((taskRows || []).filter((t) => t.source_artefact_type === 'requirement'));
      } catch (taskErr) {
        console.error('[RequirementsRegister] approval tasks load failed', taskErr);
        setApprovalTasks([]);
      }

      try {
        const pending = await fetchPendingChangeRequests(project.id);
        setPendingChanges(pending);
      } catch {
        setPendingChanges([]);
      }

      const nodeIds = [...new Set(reqs.map((r) => r.process_node_id).filter(Boolean))];
      if (nodeIds.length) {
        const { data: nodes, error: nodesError } = await sb
          .from('org_process_hierarchy')
          .select('id, code, name, level, parent_id')
          .in('id', nodeIds);
        if (nodesError) throw nodesError;
        setNodesById(new Map((nodes || []).map((n) => [n.id, n])));
      } else {
        setNodesById(new Map());
      }
    } catch (e) {
      console.error('[RequirementsRegister] load failed', e);
      showToast(e.message || 'Failed to load requirements');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const byStatus = {};
    requirements.forEach((r) => {
      byStatus[r.status] = (byStatus[r.status] || 0) + 1;
    });
    return {
      byStatus,
      sized: requirements.filter((r) => r.t_shirt_size).length,
      nodesAccepted: nodeReviews.filter((n) => n.status === 'accepted').length,
      score: ootbScore(requirements),
    };
  }, [requirements, nodeReviews]);

  const approvalTaskByReq = useMemo(() => {
    const map = new Map();
    for (const t of approvalTasks) {
      map.set(t.source_artefact_id, t);
    }
    return map;
  }, [approvalTasks]);

  const filteredReqs = useMemo(() => {
    if (statusFilter === 'all') return requirements;
    return requirements.filter((r) => r.status === statusFilter);
  }, [requirements, statusFilter]);

  const nodeGroups = useMemo(() => {
    const groups = new Map();
    for (const req of requirements) {
      const nid = req.process_node_id;
      if (!nid) continue;
      if (!groups.has(nid)) {
        groups.set(nid, { nodeId: nid, node: nodesById.get(nid), reqs: [] });
      }
      groups.get(nid).reqs.push(req);
    }
    return [...groups.values()].sort((a, b) =>
      (a.node?.code || '').localeCompare(b.node?.code || '')
    );
  }, [requirements, nodesById]);

  const workflowPhase = useMemo(() => {
    if (!requirements.length) return 'empty';
    const s = stats.byStatus;
    if (s.approved || s.baselined) return 'approved';
    if (s.sa_sizing || s.sized) return 'sizing';
    if (s.bpo_review || s.bpo_accepted) return 'bpo';
    if (s.generated) return 'generated';
    return 'draft';
  }, [requirements.length, stats.byStatus]);

  async function generateRequirements() {
    if (!project?.id || !programme?.id || !scopeApproved) return;
    setSaving(true);
    try {
      const { data: scopeRows } = await sb
        .from('project_process_scope_nodes')
        .select('process_node_id')
        .eq('project_id', project.id)
        .eq('in_scope', true);
      if (!scopeRows?.length) {
        showToast('No in-scope process nodes — complete process scope first');
        return;
      }

      const { data: nodes } = await sb
        .from('org_process_hierarchy')
        .select('id, code, name, level')
        .in('id', scopeRows.map((s) => s.process_node_id));

      const targets = (nodes || []).filter((n) => n.level >= 3);
      if (!targets.length) {
        showToast('No L3/L4 nodes in scope to generate from');
        return;
      }

      const existing = requirements.length;
      const rows = targets.map((node, i) => ({
        programme_id: programme.id,
        project_id: project.id,
        process_node_id: node.id,
        level: node.level,
        order_index: existing + i + 1,
        ref: `REQ-${String(existing + i + 1).padStart(3, '0')}`,
        title: node.name,
        description: `Generated for process area ${node.code}`,
        status: 'generated',
        requirement_type: 'standard',
        ootb_classification: 'config',
        generated_at: new Date().toISOString(),
        generated_by: profile?.id || null,
      }));

      const { error } = await sb.from('requirements').insert(rows);
      if (error) throw error;
      await load();
      showToast(`Generated ${rows.length} requirements`, 'success');
    } catch (e) {
      showToast(e.message || 'Generation failed');
    } finally {
      setSaving(false);
    }
  }

  const baselineEligible = useMemo(() => {
    if (!requirements.length || baselineLockedAt) return false;
    const allApproved = requirements.every((r) => ['approved', 'baselined'].includes(r.status));
    const scopedNodes = [...new Set(requirements.map((r) => r.process_node_id).filter(Boolean))];
    const allNodesAccepted = scopedNodes.every((nid) =>
      nodeReviews.some((n) => n.process_node_id === nid && n.status === 'accepted')
    );
    return allApproved && (scopedNodes.length === 0 || allNodesAccepted);
  }, [requirements, nodeReviews, baselineLockedAt]);

  async function lockBaseline() {
    if (!project?.id || !baselineEligible) return;
    setSaving(true);
    try {
      await sb.from('requirements')
        .update({ status: 'baselined' })
        .eq('project_id', project.id)
        .eq('status', 'approved');
      const { error } = await sb.from('projects').update({
        requirements_baseline_locked_at: new Date().toISOString(),
        requirements_baseline_locked_by: profile?.id || null,
      }).eq('id', project.id);
      if (error) throw error;
      await load();
      showToast('Requirements baseline locked', 'success');
    } catch (e) {
      showToast(e.message || 'Baseline lock failed');
    } finally {
      setSaving(false);
    }
  }

  async function submitChangeRequest() {
    if (!changeRationale.trim()) return;
    setSaving(true);
    try {
      await requestRequirementChange({
        programmeId: programme.id,
        projectId: project.id,
        rationale: changeRationale,
        profileId: profile?.id,
        profileName: profile?.full_name || profile?.email,
      });
      setShowChangeRequest(false);
      setChangeRationale('');
      await load();
      showToast('Change request submitted — approve in Governance → Decisions', 'success');
    } catch (e) {
      showToast(e.message || 'Change request failed');
    } finally {
      setSaving(false);
    }
  }

  async function updateRequirement(id, patch) {
    if (baselineLockedAt) {
      showToast('Baseline is locked — raise a change request via Governance → Decisions');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...patch };
      if (patch.t_shirt_size && patch.status === 'sized') {
        payload.sized_by = profile?.id || null;
      }
      const { error } = await sb.from('requirements').update(payload).eq('id', id);
      if (error) throw error;
      setRequirements((prev) => prev.map((r) => (r.id === id ? { ...r, ...payload } : r)));
    } catch (e) {
      showToast(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  async function advanceWorkflow() {
    setSaving(true);
    try {
      if (stats.byStatus.generated > 0) {
        await startBpoReview(project.id);
        showToast('Sent to BPO review', 'success');
      } else if (stats.byStatus.bpo_review > 0) {
        const acceptedNodes = nodeReviews.filter((n) => n.status === 'accepted').map((n) => n.process_node_id);
        const toAccept = requirements.filter(
          (r) => r.status === 'bpo_review' && acceptedNodes.includes(r.process_node_id)
        );
        if (!toAccept.length) {
          showToast('Accept process nodes before advancing');
          return;
        }
        await sb.from('requirements').update({ status: 'bpo_accepted' }).in('id', toAccept.map((r) => r.id));
        showToast('Accepted requirements advanced', 'success');
      } else if (stats.byStatus.bpo_accepted > 0) {
        await sb
          .from('requirements')
          .update({ status: 'sa_sizing' })
          .eq('project_id', project.id)
          .eq('status', 'bpo_accepted');
        showToast('Requirements ready for SA sizing', 'success');
      } else if (stats.byStatus.sized > 0) {
        await sb
          .from('requirements')
          .update({ status: 'approved' })
          .eq('project_id', project.id)
          .eq('status', 'sized');
        showToast('Requirements approved', 'success');
      }
      await load();
    } catch (e) {
      showToast(e.message || 'Workflow step failed');
    } finally {
      setSaving(false);
    }
  }

  async function setNodeReview(nodeId, status) {
    setSaving(true);
    try {
      if (status === 'accepted') {
        await acceptBpoNodeReview(project.id, nodeId);
        showToast('Node accepted — BPO approval tasks completed', 'success');
        await load();
        return;
      }

      const { error } = await sb.from('requirement_node_reviews').upsert(
        {
          project_id: project.id,
          process_node_id: nodeId,
          status,
          reviewer_id: profile?.id || null,
          reviewed_at: new Date().toISOString(),
        },
        { onConflict: 'project_id,process_node_id' }
      );
      if (error) throw error;
      await load();
      showToast(`Node marked ${NODE_REVIEW_STATUS_LABEL[status] || status}`, 'success');
    } catch (e) {
      showToast(e.message || 'Node review failed');
    } finally {
      setSaving(false);
    }
  }

  function workflowActionLabel() {
    if (stats.byStatus.generated > 0) return 'Start BPO review';
    if (stats.byStatus.bpo_review > 0) return 'Advance accepted nodes';
    if (stats.byStatus.bpo_accepted > 0) return 'Start SA sizing';
    if (stats.byStatus.sized > 0) return 'Approve sized requirements';
    return null;
  }

  if (loading) return <Loader text="Loading requirements…" />;

  const actionLabel = workflowActionLabel();
  const readOnlySizing = workflowPhase === 'approved' || !!baselineLockedAt;
  const readOnly = !canWrite || readOnlySizing;

  return (
    <InitiationLayout
      title="Requirements register"
      subtitle={`${project?.name} · IN-02`}
      badge={<span className={`b ${statusBadgeClass(workflowPhase === 'approved' ? 'approved' : 'generated')}`}>{requirements.length} reqs</span>}
      actions={
        canWrite ? (
          <>
            {!requirements.length && scopeApproved && (
              <button type="button" className="btn-primary" disabled={saving} onClick={generateRequirements}>
                Generate from scope
              </button>
            )}
            {actionLabel && !baselineLockedAt && (
              <button type="button" className="btn-primary" disabled={saving} onClick={advanceWorkflow}>
                {actionLabel}
              </button>
            )}
            {baselineEligible && (
              <button type="button" className="btn-primary" disabled={saving} onClick={lockBaseline}>
                Lock baseline
              </button>
            )}
            {baselineLockedAt && canWrite && !pendingChanges.length && (
              <button type="button" className="btn-ghost" disabled={saving} onClick={() => setShowChangeRequest(true)}>
                Request change
              </button>
            )}
          </>
        ) : null
      }
    >
      {!scopeApproved && (
        <div className="gate-banner warn">
          Process scope must be approved before requirements can be generated.
        </div>
      )}

      {baselineLockedAt && pendingChanges.length > 0 && (
        <div className="gate-banner info" style={{ marginBottom: 12 }}>
          Requirements change request pending approval in Governance → Decisions.
        </div>
      )}

      {baselineLockedAt && (
        <div className="approval-banner" style={{ marginBottom: 12 }}>
          <span className="approval-banner-icon">🔒</span>
          <span className="approval-banner-text">
            Requirements baseline locked {new Date(baselineLockedAt).toLocaleDateString()}.
            Solution options assess against this baseline. Post-lock changes require a governance change request.
          </span>
        </div>
      )}

      {requirements.length > 0 && (
        <div className="m4">
          <div className="met">
            <div className="mlbl">OOTB score</div>
            <div className="mval">{stats.score != null ? `${stats.score}%` : '—'}</div>
            <div className="msub">OOTB + config</div>
          </div>
          <div className="met">
            <div className="mlbl">Sized</div>
            <div className="mval">{stats.sized}</div>
            <div className="msub">of {requirements.length}</div>
          </div>
          <div className="met">
            <div className="mlbl">Nodes accepted</div>
            <div className="mval">{stats.nodesAccepted}</div>
            <div className="msub">of {nodeReviews.length}</div>
          </div>
          <div className="met">
            <div className="mlbl">Phase</div>
            <div className="mval" style={{ fontSize: 14 }}>{workflowPhase}</div>
            <div className="msub">workflow</div>
          </div>
        </div>
      )}

      {workflowPhase === 'bpo' && nodeGroups.length > 0 && (
        <div className="card" style={{ padding: 12 }}>
          <div className="chdr"><span className="ctitle">BPO node review</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {nodeGroups.map((g) => {
              const review = nodeReviews.find((n) => n.process_node_id === g.nodeId);
              const reviewStatus = review?.status || 'pending';
              return (
                <div key={g.nodeId} className="req-node-group-hdr">
                  <div>
                    <span className="req-node-code">{g.node?.code}</span>
                    <span className="req-node-name">{g.node?.name}</span>
                    <span style={{ marginLeft: 8, fontSize: 10, color: '#888780' }}>{g.reqs.length} reqs</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Badge variant={statusBadgeClass(reviewStatus)}>{NODE_REVIEW_STATUS_LABEL[reviewStatus] || reviewStatus}</Badge>
                    {canWrite && reviewStatus !== 'accepted' && (
                      <>
                        <button type="button" className="btn-ghost" style={{ fontSize: 10 }} disabled={saving}
                          onClick={() => setNodeReview(g.nodeId, 'accepted')}>Accept</button>
                        <button type="button" className="btn-ghost" style={{ fontSize: 10 }} disabled={saving}
                          onClick={() => setNodeReview(g.nodeId, 'flagged')}>Flag</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 12 }}>
        <div className="chdr">
          <span className="ctitle">Register</span>
          <div className="filter-tabs" style={{ marginBottom: 0 }}>
            {['all', 'generated', 'bpo_review', 'bpo_accepted', 'sa_sizing', 'sized', 'approved'].map((key) => (
              <button
                key={key}
                type="button"
                className={`ftab${statusFilter === key ? ' active' : ''}`}
                onClick={() => setStatusFilter(key)}
              >
                {key === 'all' ? 'All' : REQ_STATUS_LABEL[key] || key}
                {key !== 'all' && stats.byStatus[key] ? ` (${stats.byStatus[key]})` : ''}
              </button>
            ))}
          </div>
        </div>

        {requirements.length === 0 ? (
          <p style={{ color: '#888780', fontSize: 12, padding: 8 }}>
            {scopeApproved
              ? 'No requirements yet. Generate from approved process scope.'
              : 'Approve process scope first, then generate requirements.'}
          </p>
        ) : (
          <div className="req-table-wrap">
            <table className="req-table">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Requirement</th>
                  <th>Status</th>
                  <th>OOTB tier</th>
                  <th>Size</th>
                  <th>Cost lens</th>
                </tr>
              </thead>
              <tbody>
                {filteredReqs.map((r) => (
                  <ReqRow
                    key={r.id}
                    req={r}
                    nodesById={nodesById}
                    readOnly={readOnly}
                    baselineLocked={!!baselineLockedAt}
                    saving={saving}
                    onUpdate={updateRequirement}
                    approvalTask={approvalTaskByReq.get(r.id)}
                    focused={focusReqId === r.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showChangeRequest && (
        <div className="modal-overlay" onClick={() => setShowChangeRequest(false)} role="presentation">
          <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="modal-hdr">Requirements change request</div>
            <p style={{ fontSize: 11, color: '#888780', marginBottom: 10 }}>
              Describe why the baseline must change. A governance approver must accept this in Decisions before edits reopen.
            </p>
            <textarea
              className="form-input"
              rows={4}
              value={changeRationale}
              onChange={(e) => setChangeRationale(e.target.value)}
              placeholder="e.g. Option B fit-gap surfaced a missing regulatory requirement…"
            />
            <div className="modal-foot">
              <button type="button" className="btn-ghost" onClick={() => setShowChangeRequest(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={saving || !changeRationale.trim()} onClick={submitChangeRequest}>
                Submit request
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}
    </InitiationLayout>
  );
}
